import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowRight } from 'lucide-react';

const CustomerInfoPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data from previous step (including carteGriseData for pre-filling)
  const {
    vehicleData,
    abonnement,
    franchise,
    monthlyPrice,
    firstRegistrationDate,
    mileage,
    plateNumber,
    commitmentMonths,
    carteGriseData
  } = location.state || {};
  
  // Form state - Pre-fill from carteGriseData if available
  const [firstName, setFirstName] = useState(carteGriseData?.owner_first_name || '');
  const [lastName, setLastName] = useState(carteGriseData?.owner_last_name || '');
  const [address, setAddress] = useState(carteGriseData?.owner_address || '');
  const [zipCode, setZipCode] = useState(carteGriseData?.owner_postal_code || '');
  const [city, setCity] = useState(carteGriseData?.owner_city || '');
  const [country, setCountry] = useState('Suisse');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [acceptsContract, setAcceptsContract] = useState(false);
  const [confirmsSubscription, setConfirmsSubscription] = useState(false);
  const [sellerCode, setSellerCode] = useState('');
  const [sellerLookup, setSellerLookup] = useState({ state: 'idle', name: '' }); // idle | checking | valid | invalid
  const [errors, setErrors] = useState({});

  // Debounced seller code lookup against /api/sellers/lookup/:code
  useEffect(() => {
    const code = (sellerCode || '').trim();
    if (!code) { setSellerLookup({ state: 'idle', name: '' }); return; }
    if (!/^\d{6}$/.test(code)) {
      setSellerLookup({ state: code.length === 6 ? 'invalid' : 'idle', name: '' });
      return;
    }
    setSellerLookup({ state: 'checking', name: '' });
    const timer = setTimeout(async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API_URL}/api/sellers/lookup/${code}`);
        const data = await r.json();
        if (data.valid) {
          setSellerLookup({ state: 'valid', name: `${data.first_name} ${data.last_name}` });
        } else {
          setSellerLookup({ state: 'invalid', name: '' });
        }
      } catch {
        setSellerLookup({ state: 'invalid', name: '' });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [sellerCode]);
  
  // Redirect if no data
  useEffect(() => {
    if (!vehicleData || !abonnement) {
      navigate('/abonnements');
    }
  }, [vehicleData, abonnement, navigate]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!firstName || firstName.length < 2) {
      newErrors.firstName = 'Prénom requis (min 2 caractères)';
    }
    
    if (!lastName || lastName.length < 2) {
      newErrors.lastName = 'Nom requis (min 2 caractères)';
    }
    
    if (!address || address.length < 5) {
      newErrors.address = 'Adresse complète requise';
    }
    
    if (!zipCode || !/^\d{4}$/.test(zipCode)) {
      newErrors.zipCode = 'NPA valide requis (4 chiffres)';
    }
    
    if (!city || city.length < 2) {
      newErrors.city = 'Ville requise';
    }
    
    if (!phone || phone.length < 10) {
      newErrors.phone = 'Numéro de téléphone valide requis';
    }
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email valide requis';
    }
    
    if (!acceptsTerms) {
      newErrors.acceptsTerms = 'Vous devez accepter les CGV';
    }
    
    if (!acceptsContract) {
      newErrors.acceptsContract = 'Vous devez accepter le contrat d\'abonnement';
    }
    
    if (!confirmsSubscription) {
      newErrors.confirmsSubscription = 'Vous devez confirmer votre abonnement';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Block invalid seller_code if user typed one
      if ((sellerCode || '').trim() && sellerLookup.state !== 'valid') {
        setErrors((prev) => ({ ...prev, sellerCode: 'Code vendeur inconnu. Laissez vide ou saisissez un code valide.' }));
        return;
      }
      // Navigate to summary page
      navigate('/subscription/summary', {
        state: {
          vehicleData,
          abonnement,
          franchise,
          monthlyPrice,
          firstRegistrationDate,
          mileage,
          plateNumber,
          commitmentMonths,
          customer: {
            first_name: firstName,
            last_name: lastName,
            address,
            zip_code: zipCode,
            city,
            country,
            phone,
            email
          },
          acceptsTerms,
          acceptsContract,
          sellerCode: sellerCode.trim() || null,
        }
      });
    }
  };
  
  if (!vehicleData) return null;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
      {/* Hero Section */}
      <section className="pt-20 py-16 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Souscription à votre abonnement
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            Étape 2 sur 3 : Vos informations
          </p>
        </div>
      </section>

      <div className="container max-w-4xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="flex items-center justify-center mb-8 px-2">
          <div className="flex items-center w-full max-w-md gap-1 sm:gap-2">
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#52a675] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                ✓
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-900 truncate">Véhicule</span>
            </div>
            <div className="flex-1 h-1 bg-[#52a675] min-w-[8px]"></div>
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#52a675] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                2
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-900 truncate">Client</span>
            </div>
            <div className="flex-1 h-1 bg-gray-300 min-w-[8px]"></div>
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                3
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-500 truncate">Récap.</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Informations client et facturation
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">Prénom *</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={errors.firstName ? 'border-red-500' : ''}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="lastName">Nom *</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={errors.lastName ? 'border-red-500' : ''}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="address">Adresse complète *</Label>
                <Input
                  id="address"
                  placeholder="Rue et numéro"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className={errors.address ? 'border-red-500' : ''}
                />
                {errors.address && (
                  <p className="text-sm text-red-600 mt-1">{errors.address}</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="zipCode">NPA *</Label>
                  <Input
                    id="zipCode"
                    placeholder="1234"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    className={errors.zipCode ? 'border-red-500' : ''}
                    maxLength={4}
                  />
                  {errors.zipCode && (
                    <p className="text-sm text-red-600 mt-1">{errors.zipCode}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <Label htmlFor="city">Ville *</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className={errors.city ? 'border-red-500' : ''}
                  />
                  {errors.city && (
                    <p className="text-sm text-red-600 mt-1">{errors.city}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="country">Pays *</Label>
                <Input
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+41 79 123 45 67"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && (
                  <p className="text-sm text-red-600 mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="exemple@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Seller Code (optional) */}
              <div data-testid="seller-code-field">
                <Label htmlFor="sellerCode">
                  Code vendeur <span className="text-gray-400 font-normal">(optionnel)</span>
                </Label>
                <Input
                  id="sellerCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6 chiffres"
                  value={sellerCode}
                  onChange={(e) => {
                    setSellerCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    if (errors.sellerCode) setErrors((p) => ({ ...p, sellerCode: undefined }));
                  }}
                  className={`font-mono tracking-widest ${errors.sellerCode || sellerLookup.state === 'invalid' ? 'border-red-500' : sellerLookup.state === 'valid' ? 'border-green-500' : ''}`}
                  data-testid="input-seller-code"
                />
                {sellerLookup.state === 'checking' && (
                  <p className="text-xs text-gray-500 mt-1" data-testid="seller-lookup-checking">Vérification…</p>
                )}
                {sellerLookup.state === 'valid' && (
                  <p className="text-xs text-green-700 mt-1 flex items-center gap-1" data-testid="seller-lookup-valid">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> Vendeur : <strong>{sellerLookup.name}</strong>
                  </p>
                )}
                {sellerLookup.state === 'invalid' && (
                  <p className="text-xs text-red-600 mt-1" data-testid="seller-lookup-invalid">Code vendeur inconnu.</p>
                )}
                {errors.sellerCode && (
                  <p className="text-sm text-red-600 mt-1">{errors.sellerCode}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Si un vendeur vous a recommandé CarMaintenance, indiquez son code à 6 chiffres pour lui en attribuer le mérite.
                </p>
              </div>

              {/* Acceptances */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="acceptsTerms"
                    checked={acceptsTerms}
                    onCheckedChange={setAcceptsTerms}
                  />
                  <label htmlFor="acceptsTerms" className="text-sm leading-none cursor-pointer">
                    J'accepte les{' '}
                    <a href="/cgv" target="_blank" className="text-[#52a675] underline">
                      conditions générales de vente
                    </a>{' '}
                    *
                  </label>
                </div>
                {errors.acceptsTerms && (
                  <p className="text-sm text-red-600">{errors.acceptsTerms}</p>
                )}

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="acceptsContract"
                    checked={acceptsContract}
                    onCheckedChange={setAcceptsContract}
                  />
                  <label htmlFor="acceptsContract" className="text-sm leading-none cursor-pointer">
                    J'accepte le contrat d'abonnement CarMaintenance (durée: {commitmentMonths} mois) *
                  </label>
                </div>
                {errors.acceptsContract && (
                  <p className="text-sm text-red-600">{errors.acceptsContract}</p>
                )}

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="confirmsSubscription"
                    checked={confirmsSubscription}
                    onCheckedChange={setConfirmsSubscription}
                  />
                  <label htmlFor="confirmsSubscription" className="text-sm font-semibold leading-none cursor-pointer text-[#52a675]">
                    Je confirme mon abonnement *
                  </label>
                </div>
                {errors.confirmsSubscription && (
                  <p className="text-sm text-red-600">{errors.confirmsSubscription}</p>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Retour
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#52a675] hover:bg-[#4a9566]"
                >
                  Continuer
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerInfoPage;
